import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, ShieldCheck, LogOut, Upload, ImageIcon } from 'lucide-react';
import { adminApi, logoutAdmin } from './adminApi';
import { toAssetUrl } from '@/data/runtimeApi';

const toUrl = toAssetUrl;

const TEXT_COLORS = [
  { value: '#FFFFFF', label: 'Blanco (fondos oscuros)' },
  { value: '#3A4657', label: 'Oscuro (fondos claros)' },
];

export default function AdminServicioEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [uploadingHub, setUploadingHub] = useState(false);
  const bgRef = useRef();
  const hubRef = useRef();

  const load = async () => {
    const { data } = await adminApi.get(`/servicios/${id}`);
    setItem(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const update = (patch) => setItem((cur) => ({ ...cur, ...patch }));
  const updateBanner = (patch) => setItem((cur) => ({ ...cur, hubBannerText: { ...(cur.hubBannerText || {}), ...patch } }));
  const updateSizes = (patch) => setItem((cur) => ({ ...cur, hubBannerSizes: { ...(cur.hubBannerSizes || { smallPct: 100, mainPct: 100 }), ...patch } }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        slug: item.slug,
        name: item.name,
        shortName: item.shortName,
        hubName: item.hubName,
        textColor: item.textColor,
        hubBannerText: item.hubBannerText,
        hubBannerSizes: item.hubBannerSizes,
        homeIntro: item.homeIntro,
        homeBody: item.homeBody,
        description: item.description,
        portfolioTitle: item.portfolioTitle,
        moreTitle: item.moreTitle,
      };
      const { data } = await adminApi.patch(`/servicios/${id}`, body);
      setItem(data);
      alert('Cambios guardados.');
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const uploadImg = async (field, ref, setBusy) => {
    const f = ref.current?.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await adminApi.post(`/servicios/${id}/image?field=${field}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      update({ [field]: data.url });
      if (ref.current) ref.current.value = '';
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al subir imagen');
    } finally { setBusy(false); }
  };

  const onLogout = () => { logoutAdmin(); window.location.href = '/admin/login'; };

  if (!item) return (
    <div className="min-h-screen bg-[#f4f5f7] flex items-center justify-center">
      <Loader2 className="animate-spin text-[#58677A]"/>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f4f5f7]" data-testid="admin-servicio-edit-page">
      <header className="bg-white border-b border-[#58677A]/10 sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin/servicios" className="text-[#58677A] hover:text-[#ED6B24] transition"><ArrowLeft size={18}/></Link>
            <div className="w-9 h-9 rounded-lg bg-[#ED6B24]/10 flex items-center justify-center"><ShieldCheck className="text-[#ED6B24]" size={18} /></div>
            <div>
              <p className="text-[15px] font-bold text-[#58677A] leading-tight" style={{fontFamily:'Gotham,sans-serif'}}>Editar servicio</p>
              <p className="text-[11px] text-[#58677A]/60">{item.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#ED6B24] hover:bg-[#ff8242] text-white text-sm font-semibold rounded-full px-4 py-2 disabled:opacity-60" data-testid="save-servicio-btn">
              {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} {saving ? 'Guardando…' : 'Guardar cambios'}
            </button>
            <button onClick={onLogout} className="text-xs text-[#58677A] hover:text-red-600 px-2 py-1.5 rounded-md hover:bg-red-50 inline-flex items-center gap-1" data-testid="admin-logout"><LogOut size={13}/> Salir</button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Basic info */}
        <section className="bg-white rounded-2xl border border-[#58677A]/10 p-5 sm:p-6 space-y-4">
          <h2 className="font-bold text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Identificación</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nombre (visible en Home y menú)">
              <input value={item.name || ''} onChange={(e) => update({name: e.target.value})} className={inputCls} data-testid="field-name"/>
            </Field>
            <Field label="Nombre corto (para chips o etiquetas)">
              <input value={item.shortName || ''} onChange={(e) => update({shortName: e.target.value})} className={inputCls} data-testid="field-shortName"/>
            </Field>
            <Field label="Slug (URL: /servicios/…)">
              <input value={item.slug || ''} onChange={(e) => update({slug: e.target.value})} className={`${inputCls} font-mono text-sm`} data-testid="field-slug"/>
              <p className="text-[10px] text-orange-600 mt-1">⚠️ Cambiar el slug modifica la URL pública. Los proyectos referenciados quedarán sin categoría.</p>
            </Field>
            <Field label="Color de texto sobre las imágenes">
              <select value={item.textColor || '#FFFFFF'} onChange={(e) => update({textColor: e.target.value})} className={inputCls} data-testid="field-textColor">
                {TEXT_COLORS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </Field>
          </div>
        </section>

        {/* Images */}
        <section className="bg-white rounded-2xl border border-[#58677A]/10 p-5 sm:p-6 space-y-5">
          <h2 className="font-bold text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Imágenes</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <ImagePicker
              label="Fondo del bloque de Home (aparece detrás del menú interactivo)"
              value={item.bgImage}
              inputRef={bgRef}
              onUpload={() => uploadImg('bgImage', bgRef, setUploadingBg)}
              busy={uploadingBg}
              testId="bgImage"
            />
            <ImagePicker
              label="Banner del hub y de la página de categoría"
              value={item.hubBanner}
              inputRef={hubRef}
              onUpload={() => uploadImg('hubBanner', hubRef, setUploadingHub)}
              busy={uploadingHub}
              testId="hubBanner"
            />
          </div>
          <p className="text-[11px] text-[#58677A]/60">Las imágenes se optimizan automáticamente (máx 1920 px, JPG 82%).</p>
        </section>

        {/* Hub banner text overlay */}
        <section className="bg-white rounded-2xl border border-[#58677A]/10 p-5 sm:p-6 space-y-4">
          <h2 className="font-bold text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Texto superpuesto del banner</h2>
          <p className="text-xs text-[#58677A]/70">Se muestra sobre <strong>hubBanner</strong> en <code>/servicios-proyectos</code> y <code>/servicios/{item.slug}</code>. Deja vacías las líneas superior o inferior si no las necesitas.</p>
          <div className="grid gap-3">
            <Field label="Línea superior (opcional, minúsculas / small caps)">
              <input value={item.hubBannerText?.top || ''} onChange={(e) => updateBanner({top: e.target.value})} className={inputCls} placeholder="ej. IMPLEMENTAMOS IDEAS PARA" data-testid="field-banner-top"/>
            </Field>
            <Field label="Línea principal (grande, obligatoria)">
              <input value={item.hubBannerText?.main || ''} onChange={(e) => updateBanner({main: e.target.value})} className={`${inputCls} font-bold`} placeholder="ej. EVENTOS" data-testid="field-banner-main"/>
            </Field>
            <Field label="Línea inferior (opcional)">
              <input value={item.hubBannerText?.bottom || ''} onChange={(e) => updateBanner({bottom: e.target.value})} className={inputCls} placeholder="ej. AUTÉNTICOS & EXPERIENCIAS DE MARCA ÚNICAS" data-testid="field-banner-bottom"/>
            </Field>
          </div>

          {/* Tamaño tipográfico por banner (individual) */}
          <div className="mt-5 pt-5 border-t border-[#58677A]/10 space-y-4">
            <p className="text-xs font-bold text-[#58677A] uppercase tracking-wider">Tamaño tipográfico de este banner</p>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="text-xs text-[#58677A]/70">Texto pequeño (líneas superior/inferior)</label>
                  <span className="text-[11px] font-mono bg-[#ED6B24]/10 text-[#ED6B24] rounded px-2 py-0.5">{item.hubBannerSizes?.smallPct || 100}%</span>
                </div>
                <input
                  type="range"
                  min={60}
                  max={180}
                  value={item.hubBannerSizes?.smallPct || 100}
                  onChange={(e) => updateSizes({ smallPct: parseInt(e.target.value) })}
                  className="w-full accent-[#ED6B24]"
                  data-testid="field-smallPct"
                />
              </div>
              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="text-xs text-[#58677A]/70">Texto principal (línea grande)</label>
                  <span className="text-[11px] font-mono bg-[#ED6B24]/10 text-[#ED6B24] rounded px-2 py-0.5">{item.hubBannerSizes?.mainPct || 100}%</span>
                </div>
                <input
                  type="range"
                  min={60}
                  max={180}
                  value={item.hubBannerSizes?.mainPct || 100}
                  onChange={(e) => updateSizes({ mainPct: parseInt(e.target.value) })}
                  className="w-full accent-[#ED6B24]"
                  data-testid="field-mainPct"
                />
              </div>
            </div>
            <p className="text-[10px] text-[#58677A]/50">Los cambios afectan solo a este banner. Rango 60% – 180%.</p>
          </div>
        </section>

        {/* Home texts */}
        <section className="bg-white rounded-2xl border border-[#58677A]/10 p-5 sm:p-6 space-y-4">
          <h2 className="font-bold text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Textos del bloque Home</h2>
          <Field label="Intro (párrafo grande — pulsa Enter para separar párrafos)">
            <textarea value={item.homeIntro || ''} onChange={(e) => update({homeIntro: e.target.value})} rows={4} className={`${inputCls} leading-relaxed`} placeholder="Pulsa Enter para crear un nuevo párrafo." data-testid="field-homeIntro"/>
          </Field>
          <Field label="Cuerpo (un párrafo por línea)">
            <textarea
              value={(item.homeBody || []).join('\n')}
              onChange={(e) => update({homeBody: e.target.value.split('\n').filter((l) => l.trim() !== '')})}
              rows={5}
              className={`${inputCls} leading-relaxed`}
              placeholder="Cada línea en blanco separa párrafos."
              data-testid="field-homeBody"
            />
          </Field>
        </section>

        {/* Category page texts */}
        <section className="bg-white rounded-2xl border border-[#58677A]/10 p-5 sm:p-6 space-y-4">
          <h2 className="font-bold text-[#58677A]" style={{fontFamily:'Gotham,sans-serif'}}>Página de categoría (/servicios/{item.slug})</h2>
          <Field label="Descripción larga (pulsa Enter para separar párrafos)">
            <textarea value={item.description || ''} onChange={(e) => update({description: e.target.value})} rows={6} className={`${inputCls} leading-relaxed`} placeholder="Cada bloque separado por un Enter aparecerá como un párrafo distinto." data-testid="field-description"/>
          </Field>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Título de portafolio">
              <input value={item.portfolioTitle || ''} onChange={(e) => update({portfolioTitle: e.target.value})} className={inputCls} data-testid="field-portfolioTitle"/>
            </Field>
            <Field label="Título de sección 'más'">
              <input value={item.moreTitle || ''} onChange={(e) => update({moreTitle: e.target.value})} className={inputCls} data-testid="field-moreTitle"/>
            </Field>
          </div>
        </section>

        <div className="flex justify-end">
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-[#ED6B24] hover:bg-[#ff8242] text-white text-sm font-semibold rounded-full px-5 py-2.5 disabled:opacity-60" data-testid="save-servicio-btn-bottom">
            {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </main>
    </div>
  );
}

const inputCls = 'w-full border border-[#58677A]/20 rounded-lg px-3 py-2 focus:outline-none focus:border-[#ED6B24] focus:ring-2 focus:ring-[#ED6B24]/10 bg-white text-[#58677A]';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs text-[#58677A]/70 block mb-1">{label}</span>
    {children}
  </label>
);

const ImagePicker = ({ label, value, inputRef, onUpload, busy, testId }) => (
  <div>
    <p className="text-xs text-[#58677A]/70 mb-2">{label}</p>
    <div className="aspect-[16/10] w-full bg-[#f4f5f7] rounded-xl overflow-hidden border border-[#58677A]/10 mb-3 relative">
      {value ? (
        <img src={toUrl(value)} alt="" className="w-full h-full object-cover" data-testid={`preview-${testId}`}/>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-[#58677A]/40 gap-1">
          <ImageIcon size={28}/><span className="text-xs">Sin imagen</span>
        </div>
      )}
    </div>
    <div className="flex items-center gap-2">
      <input ref={inputRef} type="file" accept="image/*" className="text-xs text-[#58677A] flex-1 file:mr-2 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#ED6B24]/10 file:text-[#ED6B24] file:font-semibold file:cursor-pointer" data-testid={`file-${testId}`}/>
      <button onClick={onUpload} disabled={busy} className="inline-flex items-center gap-1.5 bg-[#ED6B24] hover:bg-[#ff8242] text-white text-xs font-semibold rounded-md px-3 py-1.5 disabled:opacity-60" data-testid={`upload-${testId}`}>
        {busy ? <Loader2 size={12} className="animate-spin"/> : <Upload size={12}/>} Subir
      </button>
    </div>
  </div>
);
